import React, { useState, useEffect } from 'react';
import { Home, DollarSign, Map, Calendar, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';

interface LoneStayPreferences {
  budget: {
    min: number;
    max: number;
  };
  neighborhoods: string[];
  moveInDate: string;
  amenities: string[];
  propertyTypes: string[];
}

export function LoneStayPage() {
  const navigate = useNavigate();
  const [preferences, setPreferences] = useState<LoneStayPreferences>({
    budget: { min: 2000, max: 3000 },
    neighborhoods: [],
    moveInDate: '',
    amenities: [],
    propertyTypes: ['studio', 'one_bedroom']
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [matches, setMatches] = useState<any[]>([]);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/auth');
        return;
      }

      const { data, error } = await supabase
        .from('user_preferences')
        .select('preferences')
        .eq('user_id', user.id)
        .eq('type', 'solo')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) {
        if (error.code !== 'PGRST116') {
          console.error('Error loading preferences:', error);
        }
      } else if (data?.preferences?.loneStay) {
        setPreferences(data.preferences.loneStay);
      }

      await fetchMatches();
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMatches = async () => {
    try {
      const { data } = await supabase
        .from('listings')
        .select(`
          *,
          locations (
            neighborhood,
            borough,
            zip_code
          ),
          listing_transit (
            distance_meters,
            walk_minutes,
            transit_stops (
              name,
              type,
              lines
            )
          )
        `)
        .eq('bedrooms', 1)
        .eq('is_available', true)
        .gte('price', preferences.budget.min)
        .lte('price', preferences.budget.max)
        .order('created_at', { ascending: false });

      setMatches(data || []);
    } catch (error) {
      console.error('Error fetching matches:', error);
    }
  };

  const savePreferences = async () => {
    try {
      setSaving(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { error } = await supabase
        .from('user_preferences')
        .upsert({
          user_id: user.id,
          preferences: {
            loneStay: preferences,
            type: 'solo'
          },
          type: 'solo'
        });

      if (error) throw error;
      await fetchMatches();
    } catch (error) {
      console.error('Error saving preferences:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="animate-pulse">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">LoneStay - Solo Living</h1>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Budget Range
              </label>
              <div className="flex items-center gap-4">
                <div className="relative flex-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="number"
                    value={preferences.budget.min}
                    onChange={(e) => setPreferences(prev => ({
                      ...prev,
                      budget: { ...prev.budget, min: parseInt(e.target.value) }
                    }))}
                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                <span className="text-gray-500">to</span>
                <div className="relative flex-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="number"
                    value={preferences.budget.max}
                    onChange={(e) => setPreferences(prev => ({
                      ...prev,
                      budget: { ...prev.budget, max: parseInt(e.target.value) }
                    }))}
                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Move-in Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="date"
                  value={preferences.moveInDate}
                  onChange={(e) => setPreferences(prev => ({
                    ...prev,
                    moveInDate: e.target.value
                  }))}
                  className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Property Types
              </label>
              <div className="grid grid-cols-2 gap-4">
                {['studio', 'one_bedroom', 'loft', 'micro_apartment'].map(type => (
                  <label key={type} className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={preferences.propertyTypes.includes(type)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setPreferences(prev => ({
                            ...prev,
                            propertyTypes: [...prev.propertyTypes, type]
                          }));
                        } else {
                          setPreferences(prev => ({
                            ...prev,
                            propertyTypes: prev.propertyTypes.filter(t => t !== type)
                          }));
                        }
                      }}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="text-gray-700 capitalize">{type.replace('_', ' ')}</span>
                  </label>
                ))}
              </div>
            </div>

            <button
              onClick={savePreferences}
              disabled={saving}
              className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Preferences'}
            </button>
          </div>
        </div>

        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-900">Available Properties</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {matches.map((listing) => (
              <div key={listing.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                {listing.images[0] && (
                  <img 
                    src={listing.images[0]} 
                    alt={listing.title}
                    className="w-full h-48 object-cover"
                  />
                )}
                
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{listing.title}</h3>
                  <div className="flex items-center gap-2 text-gray-600 mb-4">
                    <Map className="w-4 h-4" />
                    <span>{listing.locations.neighborhood}</span>
                  </div>
                  <div className="text-xl font-bold text-indigo-600 mb-4">
                    ${listing.price.toLocaleString()}/month
                  </div>
                  <a 
                    href={listing.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block w-full bg-indigo-600 text-white text-center px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors"
                  >
                    View Details
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}